import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { AppointmentCard } from "@/components/appointments/AppointmentCard";
import { toast } from "sonner";
import { Calendar, Clock, CheckCircle, XCircle, Plus } from "lucide-react";

const API_BASE = (import.meta as any)?.env?.VITE_API_BASE || "";
function buildUrl(path: string) {
  if (!API_BASE) return path;
  const base = API_BASE.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

interface Appointment {
  id: string;
  status: string; // 'booked' | 'completed' | 'cancelled' (current backend)
  slot?: {
    id?: string;
    startAt?: string;
    endAt?: string;
    slotType?: string;
  };
  area?: {
    id?: string;
    name?: string;
  } | null;
  address?: {
    id?: string;
    line1?: string;
    city?: string;
  };
  assignedExec?: {
    id?: string;
    publicId?: string;
    name?: string;
  } | null;
  measurementVersions?: {
    id: string;
    versionNo: number;
  }[];
}

export default function CustomerAppointmentsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  async function safeJson(res: Response) {
    try {
      return await res.json();
    } catch (err) {
      const text = await res.text().catch(() => "");
      return { _text: text || "" };
    }
  }

  async function fetchAppointments() {
    setLoading(true);
    try {
      const response = await fetch(buildUrl("/api/customer/appointments"), { credentials: "same-origin" });
      const data: any = await safeJson(response);
      if (!response.ok) throw new Error(data?.error || "Failed to fetch appointments");
      setAppointments(data.appointments || []);
    } catch (error) {
      console.error("[Appointments] fetch error", error);
      toast.error(error instanceof Error ? error.message : "Failed to load appointments");
    } finally {
      setLoading(false);
    }
  }

  async function cancelAppointment(id: string) {
    setCancellingId(id);
    try {
      const res = await fetch(buildUrl(`/api/customer/appointments/${id}/cancel`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
      });
      const data: any = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || "Failed to cancel appointment");
      toast.success("Appointment cancelled");
      await fetchAppointments();
    } catch (err) {
      console.error("[Appointments] cancel error", err);
      toast.error(err instanceof Error ? err.message : "Failed to cancel");
    } finally {
      setCancellingId(null);
    }
  }

  const grouped = useMemo(() => {
    const lower = (s: string) => (s || "").toLowerCase();
    const upcoming = appointments.filter((a) => lower(a.status) === "booked");
    const completed = appointments.filter((a) => lower(a.status) === "completed");
    const cancelled = appointments.filter((a) => lower(a.status) === "cancelled");
    const pending: Appointment[] = []; // no pending states with current schema
    return { upcoming, pending, completed, cancelled };
  }, [appointments]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="My Appointments" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Appointments"
        actions={
          <Button onClick={() => navigate("/customer/measurements/book")}>
            <Plus className="h-4 w-4 mr-2" />
            Book New Appointment
          </Button>
        }
      />

      <Tabs defaultValue="upcoming" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="upcoming" className="gap-2">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Upcoming</span>
            {grouped.upcoming.length > 0 && (
              <span className="ml-1 text-xs bg-primary/20 px-1.5 rounded-full">
                {grouped.upcoming.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Pending Review</span>
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Completed</span>
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="gap-2">
            <XCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Cancelled</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-6">
          {grouped.upcoming.length > 0 ? (
            <div className="space-y-4">
              {grouped.upcoming.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onView={() => navigate(`/customer/appointments/${appointment.id}`)}
                  onCancel={() => cancelAppointment(appointment.id)}
                  disabledActionId={cancellingId}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={Clock}
                  title="No Upcoming Appointments"
                  description="You don't have any upcoming measurement visits scheduled."
                  action={
                    <Button onClick={() => navigate("/customer/measurements/book")}>
                      <Plus className="h-4 w-4 mr-2" />
                      Book Appointment
                    </Button>
                  }
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="pending" className="mt-6">
          {grouped.pending.length > 0 ? (
            <div className="space-y-4">
              {grouped.pending.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onView={() => navigate(`/customer/appointments/${appointment.id}`)}
                  disabledActionId={cancellingId}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={Calendar}
                  title="No Pending Reviews"
                  description="No measurements are waiting for your review."
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          {grouped.completed.length > 0 ? (
            <div className="space-y-4">
              {grouped.completed.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onView={() => navigate(`/customer/appointments/${appointment.id}`)}
                  disabledActionId={cancellingId}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={CheckCircle}
                  title="No Completed Appointments"
                  description="Your completed appointments will appear here."
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="mt-6">
          {grouped.cancelled.length > 0 ? (
            <div className="space-y-4">
              {grouped.cancelled.map((appointment) => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onView={() => navigate(`/customer/appointments/${appointment.id}`)}
                  disabledActionId={cancellingId}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <EmptyState
                  icon={XCircle}
                  title="No Cancelled Appointments"
                  description="You have no cancelled appointments."
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}